import React, { useState, useEffect, useCallback } from 'react';
import {
  PieChart, Pie, Cell, Tooltip as RechartTooltip, ResponsiveContainer, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ReferenceLine,
} from 'recharts';
import { TrendingUp, LayoutGrid, Download, BarChart2 } from 'lucide-react';
import { reportsAPI } from '@/lib/api';
import SeasonalityTab from '@/components/reports/SeasonalityTab';
import FilterControls from '@/components/reports/FilterControls';
import { toast } from 'sonner';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtShort = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => `${new Date().toISOString().slice(0, 7)}-01`;
const nDaysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const weekStart = () => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); return d.toISOString().slice(0, 10); };
const yearStart = () => `${new Date().getFullYear()}-01-01`;

const PERIOD_PRESETS = [
  { label: 'Today',       start: today,    end: today },
  { label: 'This Week',   start: weekStart, end: today },
  { label: 'This Month',  start: monthStart, end: today },
  { label: 'Last 90 Days', start: () => nDaysAgo(90), end: today },
  { label: 'This Year',   start: yearStart, end: today },
  { label: 'Custom Range', start: null,    end: null },
];

const MODE_COLORS = { cash: '#16a34a', upi: '#2563eb', card: '#9333ea', khata: '#f97316', split: '#6b7280' };
const CAT_COLORS = ['#16a34a', '#2563eb', '#9333ea', '#f97316', '#06b6d4', '#ec4899', '#84cc16', '#f59e0b'];

function downloadCSV(rows, filename) {
  if (!rows.length) return;
  const header = Object.keys(rows[0]).join(',');
  const body = rows.map(r => Object.values(r).map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}

// ── Date range bar (shared) ───────────────────────────────────────────────────
function DateRangeBar({ startDate, endDate, setStartDate, setEndDate, activePeriod, setActivePeriod, onApply, extraControls }) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {PERIOD_PRESETS.map((p, i) => (
          <button key={p.label} onClick={() => {
            setActivePeriod(i);
            if (p.start) { setStartDate(p.start()); setEndDate(p.end()); onApply(p.start(), p.end()); }
          }}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
              activePeriod === i ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-foreground border-border hover:bg-muted'
            }`}>
            {p.label}
          </button>
        ))}
        {extraControls}
      </div>
      {activePeriod === 5 && (
        <div className="flex items-center gap-4 text-sm border border-border rounded-xl p-4 bg-card">
          <label className="flex items-center gap-2 text-muted-foreground">
            From
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="border border-border rounded-md px-2 py-1 text-foreground" />
          </label>
          <label className="flex items-center gap-2 text-muted-foreground">
            To
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="border border-border rounded-md px-2 py-1 text-foreground" />
          </label>
          <button onClick={() => onApply(startDate, endDate)}
            className="px-4 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium">Apply</button>
        </div>
      )}
    </div>
  );
}

// ── Hourly Sales Heatmap ──────────────────────────────────────────────────────
const DAY_LABELS  = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOUR_LABELS = Array.from({ length: 24 }, (_, i) => {
  if (i === 0)  return '12am';
  if (i < 12)   return `${i}am`;
  if (i === 12) return '12pm';
  return `${i - 12}pm`;
});
const HEATMAP_HOURS = Array.from({ length: 15 }, (_, i) => i + 7); // 7am–9pm

function HourlySalesHeatmap({ data, loading }) {
  const [tooltip, setTooltip] = useState(null);

  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border p-5">
        <div className="h-5 w-48 bg-muted rounded animate-pulse mb-4" />
        <div className="h-64 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  const cells      = data?.cells || [];
  const maxRevenue = data?.max_revenue || 0;
  const cellMap    = {};
  for (const c of cells) cellMap[`${c.hour}-${c.weekday}`] = c;
  const isEmpty = maxRevenue === 0;

  return (
    <div className="bg-card rounded-xl border border-border p-5 relative">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">Hourly Sales Heatmap</h3>
        {data?.peak_hour != null && (
          <span className="text-xs text-muted-foreground">
            Busiest: {HOUR_LABELS[data.peak_hour]} on {DAY_LABELS[data.peak_weekday]}
          </span>
        )}
      </div>

      {isEmpty ? (
        <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">No sales data for this period</div>
      ) : (
        <div className="overflow-x-auto">
          <div className="grid gap-0.5" style={{ gridTemplateColumns: '2.5rem repeat(7, minmax(0, 1fr))' }}>
            {/* Day headers */}
            <div />
            {DAY_LABELS.map(d => (
              <div key={d} className="text-center text-xs text-muted-foreground py-1 font-medium">{d}</div>
            ))}
            {/* Hour rows — 7am to 9pm only */}
            {HEATMAP_HOURS.map(hour => (
              <React.Fragment key={hour}>
                <div className="text-right text-xs text-muted-foreground pr-2 flex items-center justify-end h-7">
                  {HOUR_LABELS[hour]}
                </div>
                {Array.from({ length: 7 }, (_, weekday) => {
                  const cell    = cellMap[`${hour}-${weekday}`] || { revenue: 0, count: 0 };
                  const opacity = maxRevenue > 0 ? cell.revenue / maxRevenue : 0;
                  return (
                    <div
                      key={weekday}
                      className="h-7 rounded-sm cursor-default"
                      style={{ backgroundColor: `rgba(16,185,129,${(0.07 + opacity * 0.93).toFixed(3)})` }}
                      onMouseEnter={e => setTooltip({ hour, weekday, revenue: cell.revenue, count: cell.count, x: e.clientX, y: e.clientY })}
                      onMouseLeave={() => setTooltip(null)}
                    />
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {tooltip && (
        <div
          className="fixed z-50 bg-card border border-border rounded-lg shadow-md px-3 py-2 text-sm pointer-events-none"
          style={{ left: tooltip.x + 12, top: tooltip.y - 48 }}
        >
          <p className="font-medium">{HOUR_LABELS[tooltip.hour]}, {DAY_LABELS[tooltip.weekday]}</p>
          <p className="text-emerald-600">{fmt(tooltip.revenue)}</p>
          <p className="text-muted-foreground text-xs">{tooltip.count} bill{tooltip.count !== 1 ? 's' : ''}</p>
        </div>
      )}
    </div>
  );
}

// ── Bill Size Distribution ────────────────────────────────────────────────────
const BillTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-card border border-border rounded-lg shadow-md px-3 py-2 text-sm">
      <p className="font-medium mb-1">{label}</p>
      <p className="text-foreground">{d.count} bill{d.count !== 1 ? 's' : ''}</p>
      <p className="text-emerald-600">{fmt(d.total_revenue)} revenue</p>
    </div>
  );
};

function BillSizeDistribution({ data, loading }) {
  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border p-5 flex flex-col">
        <div className="h-5 w-44 bg-muted rounded animate-pulse mb-4" />
        <div className="flex-1 min-h-[200px] bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  const buckets = data || [];
  const hasData = buckets.some(b => b.count > 0);

  return (
    <div className="bg-card rounded-xl border border-border p-5 flex flex-col">
      <h3 className="font-semibold mb-4">Bill Size Distribution</h3>
      {!hasData ? (
        <div className="flex-1 min-h-[200px] flex items-center justify-center text-muted-foreground text-sm">No sales data for this period</div>
      ) : (
        <div className="flex-1 min-h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={buckets} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <RechartTooltip content={<BillTooltip />} />
              <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ── Product Co-Purchase Pairs ─────────────────────────────────────────────────
function ProductCoPurchasePairs({ data, loading }) {
  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border p-5">
        <div className="h-5 w-44 bg-muted rounded animate-pulse mb-4" />
        {[...Array(5)].map((_, i) => <div key={i} className="h-10 bg-muted rounded-lg animate-pulse mb-2" />)}
      </div>
    );
  }

  const pairs = data?.pairs || [];
  const total = data?.multi_item_bill_count ?? 0;

  return (
    <div className="bg-card rounded-xl border border-border p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold">Frequently Bought Together</h3>
        {total > 0 && (
          <span className="text-xs text-muted-foreground">from {total} multi-item bill{total !== 1 ? 's' : ''}</span>
        )}
      </div>

      {pairs.length === 0 ? (
        <div className="h-48 flex items-center justify-center text-muted-foreground text-sm text-center px-4">
          Not enough data — need bills with 2+ different items
        </div>
      ) : (
        <ol className="space-y-3">
          {pairs.map((pair, i) => (
            <li key={i} className="flex items-center gap-3">
              <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground shrink-0">
                {i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-sm font-medium truncate">{pair.product_a_name} + {pair.product_b_name}</p>
                  <span className="text-xs font-semibold text-emerald-600 shrink-0">{pair.count}×</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.min(pair.pct_of_multi_item_bills, 100)}%` }} />
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{pair.pct_of_multi_item_bills}% of multi-item bills</p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ── Sales Report Tab ──────────────────────────────────────────────────────────
function SalesReportTab() {
  const [startDate, setStartDate] = useState(monthStart());
  const [endDate, setEndDate] = useState(today());
  const [activePeriod, setActivePeriod] = useState(2);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [heatmapData, setHeatmapData]       = useState(null);
  const [heatmapLoading, setHeatmapLoading] = useState(true);
  const [billData, setBillData]             = useState(null);
  const [billLoading, setBillLoading]       = useState(true);
  const [pairsData, setPairsData]           = useState(null);
  const [pairsLoading, setPairsLoading]     = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [categoryFilter, setCategoryFilter]   = useState('All');
  const [categoryOptions, setCategoryOptions] = useState([]);

  const load = useCallback(async (s, e, catalogId) => {
    setLoading(true);
    try {
      const res = await reportsAPI.getSalesReport(s || startDate, e || endDate, catalogId);
      setData(res.data);
    } catch {
      toast.error('Failed to load sales report');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const loadAnalytics = useCallback(async (s, e, catalogId) => {
    const sd = s || startDate;
    const ed = e || endDate;
    setHeatmapLoading(true);
    setBillLoading(true);
    setPairsLoading(true);
    const [heatmapRes, billRes, pairsRes] = await Promise.allSettled([
      reportsAPI.getHourlyHeatmap(sd, ed, catalogId),
      reportsAPI.getBillSizeDistribution(sd, ed, catalogId),
      reportsAPI.getProductPairs(sd, ed, catalogId),
    ]);
    if (heatmapRes.status === 'fulfilled') setHeatmapData(heatmapRes.value.data);
    else { toast.error('Failed to load hourly heatmap'); setHeatmapData(null); }
    setHeatmapLoading(false);
    if (billRes.status === 'fulfilled') setBillData(billRes.value.data);
    else { toast.error('Failed to load bill distribution'); setBillData(null); }
    setBillLoading(false);
    if (pairsRes.status === 'fulfilled') setPairsData(pairsRes.value.data);
    else { toast.error('Failed to load product pairs'); setPairsData(null); }
    setPairsLoading(false);
  }, [startDate, endDate]);

  useEffect(() => { load(startDate, endDate); }, []); // eslint-disable-line
  useEffect(() => { loadAnalytics(startDate, endDate); }, []); // eslint-disable-line
  useEffect(() => {
    reportsAPI.getCategories(monthStart(), today())
      .then(r => setCategoryOptions((r.data?.categories || []).map(c => c.category)))
      .catch(() => {});
  }, []); // eslint-disable-line

  const handleProductSelect = useCallback((product) => {
    setSelectedProduct(product);
    if (product.category) setCategoryFilter(product.category);
    load(startDate, endDate, product.catalog_id);
    loadAnalytics(startDate, endDate, product.catalog_id);
  }, [startDate, endDate, load, loadAnalytics]);

  const handleProductClear = useCallback(() => {
    setSelectedProduct(null);
    setCategoryFilter('All');
    load(startDate, endDate, undefined);
    loadAnalytics(startDate, endDate, undefined);
  }, [startDate, endDate, load, loadAnalytics]);

  const pieData = (data?.payment_breakdown || []).map(p => ({
    name: p.mode.charAt(0).toUpperCase() + p.mode.slice(1),
    value: p.amount,
  }));

  return (
    <div className="space-y-6">
      <DateRangeBar
        startDate={startDate} endDate={endDate}
        setStartDate={setStartDate} setEndDate={setEndDate}
        activePeriod={activePeriod} setActivePeriod={setActivePeriod}
        onApply={(s, e) => { load(s, e, selectedProduct?.catalog_id); loadAnalytics(s, e, selectedProduct?.catalog_id); }}
        extraControls={
          <FilterControls
            selectedProduct={selectedProduct}
            onProductSelect={handleProductSelect}
            onProductClear={handleProductClear}
            categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter}
            categoryOptions={categoryOptions}
          />
        }
      />

      {loading ? (
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-28 bg-muted rounded-xl animate-pulse" />)}
        </div>
      ) : (
        <>
          {/* KPI Tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-card rounded-xl border border-border p-5">
              <p className="text-sm text-muted-foreground flex items-center gap-1.5 mb-2">
                <span className="text-emerald-600">₹</span> Total Revenue
              </p>
              <p className="text-3xl font-bold text-emerald-600">{fmt(data?.total_revenue)}</p>
            </div>
            <div className="bg-card rounded-xl border border-border p-5">
              <p className="text-sm text-muted-foreground mb-2">Total GST Collected</p>
              <p className="text-3xl font-bold text-amber-500">{fmt(data?.total_gst)}</p>
            </div>
            <div className="bg-card rounded-xl border border-border p-5">
              <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1.5">
                <TrendingUp className="h-4 w-4" /> Total Transactions
              </p>
              <p className="text-3xl font-bold text-foreground">{data?.total_transactions ?? 0}</p>
            </div>
          </div>

          {/* Payment Mode + Bill Distribution */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-card rounded-xl border border-border p-5 flex flex-col">
              <h3 className="font-semibold mb-4">Payment Mode Breakdown</h3>
              {pieData.length === 0 ? (
                <div className="flex-1 min-h-[200px] flex items-center justify-center text-muted-foreground text-sm">No sales data</div>
              ) : (
                <div className="flex-1 min-h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} dataKey="value" paddingAngle={3}>
                        {pieData.map((entry, i) => (
                          <Cell key={i} fill={MODE_COLORS[entry.name.toLowerCase()] || CAT_COLORS[i % CAT_COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartTooltip formatter={(v) => fmtShort(v)} />
                      <Legend iconType="circle" iconSize={10} formatter={(v, entry) => `${v}: ${Math.round(entry.payload.value / (data?.total_revenue || 1) * 100)}%`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            <BillSizeDistribution data={billData} loading={billLoading} />
          </div>

          {/* Sales by Time of Day + Frequently Bought Together */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <HourlySalesHeatmap data={heatmapData} loading={heatmapLoading} />
            <ProductCoPurchasePairs data={pairsData} loading={pairsLoading} />
          </div>
        </>
      )}
    </div>
  );
}

// ── Category Analytics Tab ────────────────────────────────────────────────────
function CategoryAnalyticsTab() {
  const [startDate, setStartDate] = useState(monthStart());
  const [endDate, setEndDate] = useState(today());
  const [activePeriod, setActivePeriod] = useState(2);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [categoryFilter, setCategoryFilter]   = useState('All');

  const load = useCallback(async (s, e, catalogId) => {
    setLoading(true);
    try {
      const res = await reportsAPI.getCategories(s || startDate, e || endDate, catalogId);
      setData(res.data);
    } catch {
      toast.error('Failed to load category data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { load(startDate, endDate); }, []); // eslint-disable-line

  const handleProductSelect = useCallback((product) => {
    setSelectedProduct(product);
    if (product.category) setCategoryFilter(product.category);
    load(startDate, endDate, product.catalog_id);
  }, [startDate, endDate, load]);

  const handleProductClear = useCallback(() => {
    setSelectedProduct(null);
    setCategoryFilter('All');
    load(startDate, endDate, undefined);
  }, [startDate, endDate, load]);

  const categories = data?.categories || [];
  const filteredCategories = categories
    .filter(c => categoryFilter === 'All' || c.category === categoryFilter);
  const barChartHeight = Math.max(240, filteredCategories.length * 45 + 20);
  const pieData = filteredCategories.slice(0, 6).map(c => ({ name: c.category, value: c.revenue }));
  const othersRevenue = filteredCategories.slice(6).reduce((s, c) => s + c.revenue, 0);
  if (othersRevenue > 0) pieData.push({ name: 'Others', value: othersRevenue });
  const avgMargin = filteredCategories.length
    ? parseFloat((filteredCategories.reduce((sum, c) => sum + (c.margin_pct || 0), 0) / filteredCategories.length).toFixed(1))
    : 0;
  const marginData = filteredCategories.slice(0, 8);

  const stockStatus = (sku) => {
    if (sku === 0) return { label: 'Empty', cls: 'bg-red-100 text-red-700' };
    return { label: 'Active', cls: 'bg-emerald-100 text-emerald-700' };
  };

  const handleExport = () => {
    if (!filteredCategories.length) return toast.info('No data to export');
    downloadCSV(filteredCategories.map(c => ({
      Category: c.category, Revenue: c.revenue, Units_Sold: c.units_sold,
      Margin_Pct: c.margin_pct, Top_Product: c.top_product, SKUs: c.sku_count,
    })), 'category_analytics.csv');
  };

  return (
    <div className="space-y-6">
      <DateRangeBar
        startDate={startDate} endDate={endDate}
        setStartDate={setStartDate} setEndDate={setEndDate}
        activePeriod={activePeriod} setActivePeriod={setActivePeriod}
        onApply={(s, e) => load(s, e, selectedProduct?.catalog_id)}
        extraControls={
          <FilterControls
            selectedProduct={selectedProduct}
            onProductSelect={handleProductSelect}
            onProductClear={handleProductClear}
            categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter}
            categoryOptions={categories.map(c => c.category)}
          />
        }
      />

      {loading ? (
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-muted rounded-xl animate-pulse" />)}
        </div>
      ) : (
        <>
          {/* Summary Tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-card rounded-xl border border-border p-5">
              <p className="text-sm text-muted-foreground mb-2">Top Category</p>
              <p className="text-2xl font-bold text-foreground">{filteredCategories[0]?.category || '—'}</p>
              {filteredCategories[0] && <p className="text-xs text-emerald-600 mt-1">{fmt(filteredCategories[0].revenue)} revenue</p>}
            </div>
            <div className="bg-card rounded-xl border border-border p-5">
              <p className="text-sm text-muted-foreground mb-2">Categories Tracked</p>
              <p className="text-2xl font-bold text-foreground">{filteredCategories.length}</p>
            </div>
            <div className="bg-card rounded-xl border border-border p-5">
              <p className="text-sm text-muted-foreground mb-2">Total SKUs</p>
              <p className="text-2xl font-bold text-foreground">{filteredCategories.reduce((s, c) => s + (c.sku_count || 0), 0)}</p>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Revenue by Category — horizontal bar */}
            <div className="bg-card rounded-xl border border-border p-5">
              <h3 className="font-semibold mb-4">Revenue by Category</h3>
              {filteredCategories.length === 0 ? (
                <div className="h-56 flex items-center justify-center text-muted-foreground text-sm">No category data</div>
              ) : (
                <ResponsiveContainer width="100%" height={barChartHeight}>
                  <BarChart data={filteredCategories} layout="vertical" margin={{ left: 0, right: 20, top: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                    <XAxis type="number" tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: '#888' }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="category" tick={{ fontSize: 11, fill: '#888' }} width={90} tickLine={false} axisLine={false} />
                    <RechartTooltip formatter={(v) => fmtShort(v)} />
                    <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                      {filteredCategories.map((_, i) => <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Margin % by Category — donut */}
            <div className="bg-card rounded-xl border border-border p-5">
              <h3 className="font-semibold mb-4">Margin % by Category</h3>
              {marginData.length === 0 ? (
                <div className="h-56 flex items-center justify-center text-muted-foreground text-sm">No data</div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={marginData.map(c => ({ name: c.category, value: Math.max(0, c.margin_pct) }))} cx="50%" cy="45%" innerRadius={55} outerRadius={90} dataKey="value" paddingAngle={2}>
                      {marginData.map((_, i) => <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length]} />)}
                    </Pie>
                    <RechartTooltip formatter={(v) => [`${Number(v).toFixed(1)}%`, 'Gross Margin']} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Category Table */}
          <div className="bg-card rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Category Breakdown</h3>
              <button onClick={handleExport} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <Download className="h-4 w-4" /> Export CSV
              </button>
            </div>
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    {['Category', 'Revenue', 'Revenue %', 'Units Sold', 'Avg Margin', 'Top Product', 'SKUs'].map(h => (
                      <th key={h} className={`py-2 text-muted-foreground font-medium ${h === 'Category' ? 'text-left' : h === 'Top Product' ? 'text-left pl-6' : 'text-right'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredCategories.map((c, i) => {
                    const s = stockStatus(c.sku_count);
                    return (
                      <tr key={i} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-2.5 font-medium">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full" style={{ background: CAT_COLORS[i % CAT_COLORS.length] }} />
                            {c.category}
                          </div>
                        </td>
                        <td className="py-2.5 text-right font-semibold text-emerald-600">{fmt(c.revenue)}</td>
                        <td className="py-2.5 text-right text-muted-foreground">{c.revenue_pct}%</td>
                        <td className="py-2.5 text-right">{c.units_sold}</td>
                        <td className={`py-2.5 text-right font-medium ${c.margin_pct > 0 ? 'text-emerald-600' : 'text-red-500'}`}>{c.margin_pct}%</td>
                        <td className="py-2.5 pl-6 text-muted-foreground max-w-[160px] truncate">{c.top_product || '—'}</td>
                        <td className="py-2.5 text-right">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.cls}`}>{c.sku_count}</span>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredCategories.length === 0 && (
                    <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">No category data for this period</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'sales',        label: 'Sales Report',       icon: TrendingUp },
  { id: 'categories',   label: 'Category Analytics', icon: LayoutGrid },
  { id: 'seasonality',  label: 'Seasonality',        icon: BarChart2  },
];

export default function AdvancedReports() {
  const [activeTab, setActiveTab] = useState('sales');

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Advanced Reports</h1>
        <p className="text-sm text-muted-foreground">Comprehensive business analytics and reports</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-border pb-0">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border border-b-0 transition-colors -mb-px ${
                activeTab === t.id
                  ? 'bg-card border-border text-foreground'
                  : 'bg-muted/40 border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'sales'       && <SalesReportTab />}
        {activeTab === 'categories'  && <CategoryAnalyticsTab />}
        {activeTab === 'seasonality' && <SeasonalityTab />}
      </div>
    </div>
  );
}
